import NavElement from "./NavElement";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faShoppingCart} from "@fortawesome/sharp-regular-svg-icons/faShoppingCart";
import {faArrowRightFromBracket} from "@fortawesome/sharp-regular-svg-icons/faArrowRightFromBracket";
import TooltipWrapper from "../TooltipWrapper";
import makeAccountMenu from "@/app/util/makeAccountMenu";
import {MenuItem} from "./MobileMenu/MenuTypes";
import {useCookieCampusId} from "../contexts/CampusCookieContext";
import {useUserInfo} from "../contexts/userInfoContext";
import useHoverMenus, {UseHoverMenus} from "./useHoverMenus";
import NavSignInButton from "./NavSignInButton";
import {
	ACCOUNT_MENU_ID,
	CART_NAV_ITEM_ID,
} from "@/app/util/navigationMenuConstants";
import NavDropdownItemWithChildren from "./NavDropdownItemWithChildren";
import NavDropdownSubmenu from "./NavDropdownSubmenu";
import NavBarAccountSectionHeader from "./NavBarAccountSectionHeader";
import CartActiveIcon from "./CartActiveIcon";
import useSignOutHref from "./useSignOutHref";
import {faUserCircle} from "@fortawesome/sharp-regular-svg-icons/faUserCircle";
import {Suspense} from "react";

type NavBarAccountSectionProps = {
	hoverHandlers?: UseHoverMenus;
};

const AccountMenu: React.FC<UseHoverMenus> = ({
	isSubmenuOpen,
	activeSubmenu,
	onDropdownItemMouseEnter,
	onDropdownItemMouseLeave,
}) => {
	const {userInfo} = useUserInfo();
	const signOutHref = useSignOutHref();
	const campusId = useCookieCampusId();

	if (!userInfo) {
		return null;
	}

	const menuId = ACCOUNT_MENU_ID;
	const menuItems: MenuItem[] = makeAccountMenu(userInfo, campusId);
	menuItems.push({
		label: (
			<span>
				Sign Out
				<FontAwesomeIcon
					className="ml-2"
					icon={faArrowRightFromBracket}
					size="sm"
				/>
			</span>
		),
		href: signOutHref,
		id: "sign-out",
		isExternal: true,
	});

	return (
		<NavDropdownItemWithChildren
			classes={{
				dialog: "p-0 pb-4 overflow-hidden",
				root: "p-0",
				labelRoot: "px-2",
				overlayArrow: "fill-green-800 stroke-green-800",
			}}
			label={
				<div aria-label="My Account" className="h-6 w-4">
					<FontAwesomeIcon icon={faUserCircle} className="fa-1x pt-[5px]" />
				</div>
			}
			id={menuId}
			isOpen={isSubmenuOpen && activeSubmenu === menuId}
			popoverOffset={15}
			onMouseEnter={onDropdownItemMouseEnter(menuId)}
			onMouseLeave={onDropdownItemMouseLeave}
		>
			<NavBarAccountSectionHeader userInfo={userInfo} />
			<NavDropdownSubmenu menuItems={menuItems} classes={{listItems: "px-8"}} />
		</NavDropdownItemWithChildren>
	);
};

const NavBarAccountSection: React.FC<NavBarAccountSectionProps> = ({
	hoverHandlers,
}) => {
	const {userInfo, isLoading} = useUserInfo();
	const defaultHoverHandlers = useHoverMenus();

	const {
		activeSubmenu,
		isSubmenuOpen,
		onDropdownItemMouseEnter,
		onDropdownItemMouseLeave,
		...additionalHoverHandlers
	} = hoverHandlers ?? defaultHoverHandlers;

	if (isLoading) {
		return null;
	}

	if (!userInfo) {
		return <NavSignInButton />;
	}
	return (
		<Suspense>
			<AccountMenu
				activeSubmenu={activeSubmenu}
				isSubmenuOpen={isSubmenuOpen}
				onDropdownItemMouseEnter={onDropdownItemMouseEnter}
				onDropdownItemMouseLeave={onDropdownItemMouseLeave}
				{...additionalHoverHandlers}
			/>
			<TooltipWrapper
				tooltip="Cart"
				isOpen={activeSubmenu === CART_NAV_ITEM_ID && isSubmenuOpen}
			>
				<NavElement
					className="flex h-10 w-10 justify-center p-0 pt-[0.2rem]"
					href="/cart"
					aria-label="Cart"
					onHoverStart={onDropdownItemMouseEnter(CART_NAV_ITEM_ID)}
					onHoverEnd={onDropdownItemMouseLeave}
				>
					<div className="relative flex h-full items-center">
						<FontAwesomeIcon icon={faShoppingCart} size="sm" />
						<CartActiveIcon />
					</div>
				</NavElement>
			</TooltipWrapper>
		</Suspense>
	);
};

export default NavBarAccountSection;
